import { UploadListParams } from "@/services/network/type";
import { UploadService } from "@/services/network/upload";
import { useQuery } from "@tanstack/react-query";

const uploadService = new UploadService();

interface UploadListResponse {
  data: UploadDetailData[];
  total: number;
  page: number;
  limit: number;
}

interface UploadDetailData {
  id: number;
  type: string;
  uploadType: string;
  fileUploadedUrl: string;
  status: string;
  totalCount: number;
  successCount: number;
  failureCount: number;
  numberFileProcessed: number;
  errorSummaryFileUrl: string | null;
  errorSummary: string | null;
  createdBy: number;
  activeAccount: boolean;
  excelHeaders: string[];
  defaultFieldMapping: Record<string, string>;
  userFieldMapping: {
    [key: string]: string | string[];
    mandatoryFields: string[];
  };
  headerMapping: Record<string, string>;
  fileName: string;
  createdAt: string;
  updatedAt: string;
  activatedById: number | null;
  activationDate: string | null;
  uploadId: string;
}
interface ApiServiceResponse<T extends UploadListResponse | UploadDetailData> {
  status: number;
  message: string;
  data: T;
}

const useUploadList = (params: UploadListParams) => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [UploadService.QUERY_KEY.FETCH_ALL, params],
    queryFn: () => uploadService.getUploadList(params),
  });

  return {
    data,
    isLoading,
    isError,
    refetch,
  };
};

const useUploadDetail = (fileId: number | undefined) => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [UploadService.QUERY_KEY.FETCH_DETAIL, fileId],
    queryFn: async () => {
      const response = await uploadService.getUploadDetail(fileId);
      if (response.status === 200 && response.data) {
        return response as ApiServiceResponse<UploadDetailData>;
      } else {
        throw new Error(response.message || "Failed to fetch upload detail");
      }
    },
    enabled: !!fileId,
  });

  return {
    data,
    isLoading,
    isError,
    refetch,
  };
};

export { useUploadDetail, useUploadList };
export type { UploadDetailData };
